// Products component — uses Firestore (with seed fallback) and i18n
import { useMemo, useState, useEffect } from "react";
import { Search, Plus, Star, LayoutGrid, List as ListIcon, Eye, X, Check, PackagePlus, Gift, Info, ChevronRight, Flame, Clock, Utensils, ThermometerSnowflake, ZoomIn } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/lib/cart";
import { useProducts, useProductRatings, useComboSettings, useSamplePackSettings } from "@/lib/firestoreService";
import { useLang } from "@/lib/i18n";
import { useCartContext } from "@/contexts/CartContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ComboBuilderModal } from "@/components/ComboBuilderModal";
import { SamplePackModal } from "@/components/SamplePackModal";
import { track } from "@/lib/analytics";

const SamplePackDropdownButton = ({ onSelect }) => {
  // Kept uncontrolled-looking but reset right after every pick — Radix's
  // Select only fires onValueChange when the value actually changes, so
  // without this, re-picking the same already-selected pack (e.g. closing
  // the modal and reopening "Non-Veg Sample Pack" again) would silently do
  // nothing. Resetting to "" after each pick means every click is always a
  // change from the Select's point of view, so onSelect fires every time.
  const [value, setValue] = useState("");
  return (
    <Select
      value={value}
      onValueChange={(v) => {
        setValue(v);
        onSelect(v);
        setTimeout(() => setValue(""), 0);
      }}
    >
      <SelectTrigger
        data-testid="sample-pack-button"
        className="w-auto h-10 px-4 rounded-sm font-semibold text-sm border-2 border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F]/10 transition-colors bg-transparent gap-2"
      >
        <Gift className="w-4 h-4" />
        <SelectValue placeholder="Order Sample Pack" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="veg">Veg Order Sample Pack</SelectItem>
        <SelectItem value="non_veg">Non-Veg Order Sample Pack</SelectItem>
      </SelectContent>
    </Select>
  );
};

const ViewToggle = ({ view, setView }) => (
  <div className="inline-flex border-2 border-[#5A2D1D]/30 rounded-sm overflow-hidden flex-shrink-0" data-testid="view-toggle">
    <button
      type="button"
      data-testid="view-toggle-grid"
      onClick={() => setView("grid")}
      aria-pressed={view === "grid"}
      aria-label="Grid view"
      className={`p-2 transition-colors ${view === "grid" ? "bg-[#5A2D1D] text-[#F5E6CC]" : "text-[#5A2D1D] hover:bg-[#5A2D1D]/10"}`}
    >
      <LayoutGrid className="w-4 h-4" />
    </button>
    <button
      type="button"
      data-testid="view-toggle-list"
      onClick={() => setView("list")}
      aria-pressed={view === "list"}
      aria-label="List view"
      className={`p-2 transition-colors border-l-2 border-[#5A2D1D]/30 ${view === "list" ? "bg-[#5A2D1D] text-[#F5E6CC]" : "text-[#5A2D1D] hover:bg-[#5A2D1D]/10"}`}
    >
      <ListIcon className="w-4 h-4" />
    </button>
  </div>
);

export const Products = ({ onAdd, onCartOpen }) => {
  const { t, isTe } = useLang();
  const products = useProducts();
  const ratings = useProductRatings();
  const comboSettings = useComboSettings();
  const samplePackSettings = useSamplePackSettings();
  const cart = useCartContext();
  const [comboModalOpen, setComboModalOpen] = useState(false);
  const [samplePackModalType, setSamplePackModalType] = useState(null); // "veg" | "non_veg" | null
  const [viewDetailsProduct, setViewDetailsProduct] = useState(null);
  const [viewImageProduct, setViewImageProduct] = useState(null);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  // Screens under ~10" (phones + small tablets like iPad Mini, which is
  // 744px wide in portrait) default to the compact grid; 10"+ tablets and
  // up (standard iPad is 810px wide in portrait) default to the full list.
  // Browsers can't report physical screen size, so width is the closest
  // proxy — 768px sits squarely between those two reference devices.
  const [view, setView] = useState(() => (window.innerWidth < 768 ? "grid" : "list"));

  const CATEGORIES = [
    { value: "all", label: t.cta.all },
    { value: "veg", label: t.cta.veg },
    { value: "non-veg", label: t.cta.nonveg },
  ];

  const filtered = useMemo(() => {
    if (!products) return null;
    let items = products;
    if (cat !== "all") items = items.filter((p) => p.category === cat);
    if (q.trim()) {
      const ql = q.toLowerCase();
      items = items.filter(
        (p) =>
          p.name?.toLowerCase().includes(ql) ||
          (p.name_te || "").includes(q) ||
          (p.description || "").toLowerCase().includes(ql)
      );
    }
    return items;
  }, [products, cat, q]);

  // Debounced — the search box filters live on every keystroke, so tracking
  // on change would fire once per character typed. Wait for a pause instead,
  // matching what the customer actually searched for in one go.
  useEffect(() => {
    if (!q.trim()) return;
    const id = setTimeout(() => {
      track("product_search", {
        query: q.trim(),
        query_length: q.trim().length,
        result_count: filtered?.length ?? 0,
        zero_results: (filtered?.length ?? 0) === 0,
        active_category: cat,
      });
    }, 800);
    return () => clearTimeout(id);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative py-6 sm:py-8 bg-[#EFD9B4]/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-5">
          <div className="brand-ribbon mb-2">{t.products.ribbon}</div>
          <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-[#5A2D1D]">
            {t.products.h1} <span className="italic text-[#7A3B25]">{t.products.h2}</span>
          </h2>
        </div>

        <div className="mb-6 max-w-5xl mx-auto space-y-2.5">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A2D1D]/60" />
            <input
              data-testid="product-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t.cta.search_placeholder}
              className="w-full h-10 pl-10 pr-4 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-[#2E1A12] placeholder:text-[#2E1A12]/50"
            />
          </div>
          <div className="flex gap-2 flex-wrap items-center" data-testid="category-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                data-testid={`filter-${c.value}`}
                onClick={() => {
                  const resultCount = c.value === "all" ? products?.length ?? 0 : products?.filter(p => p.category === c.value).length ?? 0;
                  track("product_filter_applied", { category: c.value, previous_category: cat, result_count: resultCount });
                  setCat(c.value);
                }}
                className={`h-10 px-4 rounded-sm font-semibold text-sm transition-colors border-2 ${
                  cat === c.value
                    ? "bg-[#5A2D1D] border-[#5A2D1D] text-[#F5E6CC]"
                    : "border-[#5A2D1D]/30 text-[#5A2D1D] hover:border-[#5A2D1D]"
                }`}
              >
                {c.label}
              </button>
            ))}
            <ViewToggle view={view} setView={(v) => { track("product_view_mode_changed", { view_mode: v, previous_mode: view }); setView(v); }} />
            {comboSettings.enabled && (
              <button
                type="button"
                data-testid="create-combo-button"
                onClick={() => { setComboModalOpen(true); track("Clicked on Create Your Own Combo", { source: "products_toolbar" }); }}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-sm font-semibold text-sm border-2 border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F]/10 transition-colors"
              >
                <PackagePlus className="w-4 h-4" /> Create Your Own Combo
              </button>
            )}
            {samplePackSettings.enabled && (
              <SamplePackDropdownButton
                onSelect={(packType) => {
                  setSamplePackModalType(packType);
                  track("Clicked on Sample Pickle Pack", { pack_type: packType, source: "products_toolbar" });
                }}
              />
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-[#2E1A12]/70" data-testid="empty-products">
            {t.products.empty}
          </div>
        ) : view === "grid" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-5" data-testid="products-grid">
            {filtered.map((p) => (
              <ProductCardCompact
                key={p.id}
                product={p}
                isTe={isTe}
                rating={ratings[p.id]}
                onView={(prod) => {
                  setViewDetailsProduct(prod);
                  track("product_details_opened", {
                    product_id: prod.id,
                    product_name: prod.name,
                    product_slug: prod.slug,
                    category: prod.category,
                    in_stock: prod.in_stock,
                    rating: ratings[prod.id]?.avg ?? null,
                    source: "compact_grid",
                  });
                }}
                onViewImage={(prod) => setViewImageProduct(prod)}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6" data-testid="products-grid">
            {filtered.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                onAdd={onAdd}
                isTe={isTe}
                t={t}
                rating={ratings[p.id]}
                onViewDetails={() => {
                  setViewDetailsProduct(p);
                  track("product_details_opened", {
                    product_id: p.id,
                    product_name: p.name,
                    product_slug: p.slug,
                    category: p.category,
                    in_stock: p.in_stock,
                    rating: ratings[p.id]?.avg ?? null,
                    source: "list",
                  });
                }}
                onViewImage={() => setViewImageProduct(p)}
              />
            ))}
          </div>
        )}
      </div>

<Dialog open={!!viewDetailsProduct} onOpenChange={(o) => !o && setViewDetailsProduct(null)}>
        <DialogContent className="bg-[#F5E6CC] border border-[#5A2D1D]/30 rounded-sm max-w-lg w-[calc(100%-2rem)] p-0 gap-0 max-h-[90vh] overflow-y-auto [&>button]:hidden">
          {viewDetailsProduct && (
            <>
              <DialogTitle className="sr-only">
                {isTe && viewDetailsProduct.name_te ? viewDetailsProduct.name_te : viewDetailsProduct.name}
              </DialogTitle>
              <ProductDetailsModal
                product={viewDetailsProduct}
                onAdd={onAdd}
                isTe={isTe}
                t={t}
                rating={ratings[viewDetailsProduct.id]}
                onClose={() => setViewDetailsProduct(null)}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {viewImageProduct && (
        <ImageLightbox
          product={viewImageProduct}
          displayName={isTe && viewImageProduct.name_te ? viewImageProduct.name_te : viewImageProduct.name}
          onClose={() => setViewImageProduct(null)}
        />
      )}

      <ComboBuilderModal
        open={comboModalOpen}
        onClose={() => setComboModalOpen(false)}
        onAdded={() => onCartOpen?.()}
        comboSettings={comboSettings}
        products={products}
        cart={cart}
      />
      <SamplePackModal
        open={!!samplePackModalType}
        packType={samplePackModalType}
        onClose={() => setSamplePackModalType(null)}
        onAdded={() => onCartOpen?.()}
        samplePackSettings={samplePackSettings}
        products={products}
        cart={cart}
      />
    </section>
  );
};

const ProductCard = ({ product, onAdd, isTe, t, rating, onViewDetails, onViewImage }) => (
  <div
    data-testid={`product-card-${product.slug}`}
    className="group relative bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm overflow-hidden hover-lift flex flex-col"
  >
    <ProductDetails product={product} onAdd={onAdd} isTe={isTe} t={t} rating={rating} onViewDetails={onViewDetails} onViewImage={onViewImage} />
  </div>
);

// Image + name + rating + description + size selector + price + add-to-cart.
// Shared by the full list-view card and the grid view's "View" quick-look modal,
// so both paths use the exact same selection flow before adding to the cart.
const ProductDetails = ({ product, onAdd, isTe, t, rating, onAdded, onViewDetails, onViewImage }) => {
  const [size, setSize] = useState(product.sizes[0].label);
  const selectedSize = product.sizes.find((s) => s.label === size) || product.sizes[0];
  const displayName = isTe && product.name_te ? product.name_te : product.name;
  const displayDesc = isTe && product.description_te ? product.description_te : product.description;
  const soldOut = product.in_stock === false;

  const handleAdd = () => {
    onAdd(product, selectedSize);
    toast.success(`${displayName} (${selectedSize.label})`, {
      description: `${formatPrice(selectedSize.price)} • Added to cart`,
    });
    onAdded?.();
  };

  return (
    <>
      <div className="relative aspect-square overflow-hidden bg-[#EFD9B4]">
        <img
          src={product.image_url}
          alt={product.name}
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${soldOut ? "grayscale opacity-70" : ""}`}
          loading="lazy"
          decoding="async"
        />
        {soldOut && (
          <div className="absolute inset-0 bg-[#2E1A12]/20 grid place-items-center">
            <span
              data-testid={`sold-out-badge-${product.slug}`}
              className="bg-[#7A3B25] text-[#F5E6CC] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg"
            >
              Sold Out
            </span>
          </div>
        )}
        {!soldOut && product.featured && (
          <div className="absolute top-3 left-3 brand-ribbon !text-[10px]">Bestseller</div>
        )}
        <div className="absolute top-3 right-3 bg-[#F5E6CC]/95 text-[#5A2D1D] text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm border border-[#5A2D1D]/30 flex items-center gap-1.5">
          {product.category === "veg" ? (
            <>
              <span className="w-3 h-3 rounded-full bg-[#1a7a1a] border-2 border-[#1a7a1a] flex-shrink-0" />
              Veg
            </>
          ) : (
            <>
              <span className="w-0 h-0 flex-shrink-0"
                style={{ borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: "10px solid #b91c1c" }} />
              Non-Veg
            </>
          )}
        </div>
        {product.image_url && onViewImage && (
          <button
            type="button"
            onClick={onViewImage}
            aria-label="View full image"
            className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-[#5A2D1D] rounded-full p-1.5 shadow-md transition-colors z-10"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display text-xl font-bold text-[#5A2D1D]">{displayName}</h3>
          {rating && (
            <div data-testid={`product-rating-${product.slug}`} className="flex items-center gap-1 flex-shrink-0 text-[#556B2F]">
              <Star className="w-3.5 h-3.5 fill-[#556B2F]" />
              <span className="text-xs font-bold">{rating.avg.toFixed(1)}</span>
              <span className="text-[10px] text-[#2E1A12]/50">({rating.count})</span>
            </div>
          )}
        </div>
        <p className="mt-2 text-sm text-[#2E1A12]/80 leading-relaxed flex-1">{displayDesc}</p>
        {soldOut && product.stock_note && (
          <p className="mt-2 text-xs text-[#7A3B25] font-semibold italic">{product.stock_note}</p>
        )}

        <div className="mt-5 grid grid-cols-2 gap-3 items-end">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-1">
              {t.products.size}
            </label>
            <Select value={size} onValueChange={setSize} disabled={soldOut}>
              <SelectTrigger
                data-testid={`size-selector-${product.slug}`}
                className="rounded-sm border-[#5A2D1D]/40 bg-[#F5E6CC] text-[#2E1A12]"
              >
                {/* Show only the size label in the trigger — price is already
                    in the PRICE column, so we don't need to repeat it here
                    and it avoids text being clipped in narrow cards */}
                <span>{size}</span>
              </SelectTrigger>
              <SelectContent>
                {product.sizes.map((s) => (
                  <SelectItem key={s.label} value={s.label}>
                    {s.label} — {formatPrice(s.price)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#556B2F] font-bold mb-1">
              {t.products.price}
            </div>
            <div
              data-testid={`product-price-${product.slug}`}
              className="font-display text-2xl font-bold text-[#5A2D1D]"
            >
              {formatPrice(selectedSize.price)}
            </div>
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          {onViewDetails && (
            <button
              type="button"
              data-testid={`view-details-${product.slug}`}
              onClick={onViewDetails}
              className="inline-flex items-center gap-1.5 border-2 border-[#5A2D1D]/40 hover:border-[#5A2D1D] text-[#5A2D1D] px-3 py-3 rounded-sm font-semibold text-xs transition-colors flex-shrink-0 whitespace-nowrap"
            >
              <Info className="w-3.5 h-3.5" /> View Details
            </button>
          )}
          <button
            data-testid={`add-to-cart-${product.slug}`}
            disabled={soldOut}
            onClick={handleAdd}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/30 disabled:cursor-not-allowed text-[#F5E6CC] py-3 rounded-sm font-semibold transition-colors"
          >
            {soldOut ? "Sold Out" : (<><Plus className="w-4 h-4" /> {t.cta.add_to_cart}</>)}
          </button>
        </div>
      </div>
    </>
  );
};

// Compact grid card — image, name, price only. Tapping the image or the
// "View" button opens the full product details (size picker + Add to Cart)
// in a modal — the same selection flow as the list-view card.
const ProductCardCompact = ({ product, isTe, rating, onView, onViewImage }) => {
  const displayName = isTe && product.name_te ? product.name_te : product.name;
  const soldOut = product.in_stock === false;
  const cheapest = product.sizes.reduce((min, s) => (s.price < min.price ? s : min), product.sizes[0]);

  return (
    <div
      data-testid={`product-card-compact-${product.slug}`}
      className="group relative bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm overflow-hidden hover-lift flex flex-col"
    >
      {/* Image area — separate div so zoom button and details button can coexist without nesting */}
      <div className="relative w-full aspect-square overflow-hidden bg-[#EFD9B4]">
        {/* Full-area click → open details */}
        <button
          type="button"
          onClick={() => onView(product)}
          aria-label={`View ${displayName}`}
          className="absolute inset-0 w-full h-full"
        >
          <img
            src={product.image_url}
            alt={product.name}
            className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${soldOut ? "grayscale opacity-70" : ""}`}
            loading="lazy"
            decoding="async"
          />
        </button>

        {soldOut && (
          <div className="absolute inset-0 pointer-events-none bg-[#2E1A12]/20 grid place-items-center">
            <span className="bg-[#7A3B25] text-[#F5E6CC] text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-lg">
              Sold Out
            </span>
          </div>
        )}
        {rating && (
          <div className="absolute bottom-1.5 left-1.5 pointer-events-none bg-[#F5E6CC]/95 text-[#556B2F] text-[10px] font-bold px-1.5 py-0.5 rounded-sm flex items-center gap-0.5">
            <Star className="w-2.5 h-2.5 fill-[#556B2F]" />
            {rating.avg.toFixed(1)}
          </div>
        )}

        {/* Zoom icon — sibling to image button, no nesting */}
        {product.image_url && onViewImage && (
          <button
            type="button"
            onClick={() => onViewImage(product)}
            aria-label="View full image"
            className="absolute bottom-1.5 right-1.5 z-10 bg-white/90 hover:bg-white text-[#5A2D1D] rounded-full p-1 shadow-md transition-colors"
          >
            <ZoomIn className="w-3 h-3" />
          </button>
        )}
      </div>

      <div className="p-2.5 flex flex-col gap-1.5">
        <h3 className="font-display text-sm font-bold text-[#5A2D1D] truncate">{displayName}</h3>
        <div className="flex items-center justify-between gap-2">
          <div
            data-testid={`product-price-compact-${product.slug}`}
            className="text-sm font-bold text-[#7A3B25]"
          >
            From {formatPrice(cheapest.price)}
          </div>
          <button
            type="button"
            data-testid={`view-product-${product.slug}`}
            onClick={() => onView(product)}
            className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide border-2 border-[#5A2D1D] text-[#5A2D1D] px-2.5 py-1 rounded-full hover:bg-[#5A2D1D] hover:text-[#F5E6CC] transition-colors flex-shrink-0"
          >
            <Eye className="w-3 h-3" /> View
          </button>
        </div>
      </div>
    </div>
  );
};

const DETAIL_ROWS = [
  { key: "ingredients", label: "Ingredients" },
  { key: "shelf_life",  label: "Shelf Life"  },
  { key: "how_to_use",  label: "How to Serve"},
  { key: "storage",     label: "Storage"     },
];

const ProductDetailsModal = ({ product, onAdd, isTe, t, rating, onClose }) => {
  const [size, setSize] = useState(product.sizes[0].label);
  const selectedSize = product.sizes.find((s) => s.label === size) || product.sizes[0];
  const displayName  = isTe && product.name_te        ? product.name_te        : product.name;
  const displayDesc  = isTe && product.description_te ? product.description_te : product.description;
  const soldOut      = product.in_stock === false;
  const details      = product.details || {};
  const highlights   = (details.highlights || []).filter(Boolean);
  // "ingredients" is the only detail field with a Telugu counterpart today
  // (ingredients_te) — everything else in DETAIL_ROWS stays English-only.
  const displayDetails = {
    ...details,
    ingredients: (isTe && details.ingredients_te) ? details.ingredients_te : details.ingredients,
  };

  const handleAdd = () => {
    onAdd(product, selectedSize, "details_modal");
    toast.success(`${displayName} (${selectedSize.label})`, {
      description: `${formatPrice(selectedSize.price)} • Added to cart`,
    });
    onClose();
  };

  return (
    <div data-testid={`product-details-modal-${product.slug}`} className="p-5">
      {/* Header: small square image + close */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="w-24 h-24 flex-shrink-0 rounded-sm overflow-hidden bg-[#EFD9B4] border border-[#5A2D1D]/20">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name}
              className={`w-full h-full object-cover ${soldOut ? "grayscale opacity-60" : ""}`}
              decoding="async" />
          ) : (
            <div className="w-full h-full bg-[#EFD9B4]" />
          )}
        </div>
        <button type="button" onClick={onClose} aria-label="Close"
          className="ml-auto flex-shrink-0 bg-[#5A2D1D]/10 hover:bg-[#5A2D1D]/20 text-[#5A2D1D] rounded-full p-1.5 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Details table */}
      <table className="w-full text-sm border-collapse mb-5">
        <tbody>
          {/* Name row — always shown */}
          <tr className="border border-[#5A2D1D]/20">
            <td className="px-3 py-2.5 font-bold text-[#556B2F] bg-[#EFD9B4]/60 w-[35%] align-top whitespace-nowrap">Name</td>
            <td className="px-3 py-2.5 text-[#2E1A12]">
              <span className="font-semibold">{displayName}</span>
              {rating && (
                <span className="ml-2 inline-flex items-center gap-0.5 text-[#556B2F] text-xs">
                  <Star className="w-3 h-3 fill-[#556B2F]" />
                  {rating.avg.toFixed(1)}
                  <span className="text-[#2E1A12]/40">({rating.count})</span>
                </span>
              )}
              {soldOut && <span className="ml-2 text-[10px] font-bold text-[#7A3B25] uppercase tracking-wide">Sold Out</span>}
            </td>
          </tr>

          {/* Description */}
          {displayDesc && (
            <tr className="border border-[#5A2D1D]/20 border-t-0">
              <td className="px-3 py-2.5 font-bold text-[#556B2F] bg-[#EFD9B4]/60 align-top whitespace-nowrap">Description</td>
              <td className="px-3 py-2.5 text-[#2E1A12]/80 leading-relaxed">{displayDesc}</td>
            </tr>
          )}

          {/* Highlights */}
          {highlights.length > 0 && (
            <tr className="border border-[#5A2D1D]/20 border-t-0">
              <td className="px-3 py-2.5 font-bold text-[#556B2F] bg-[#EFD9B4]/60 align-top whitespace-nowrap">Highlights</td>
              <td className="px-3 py-2.5">
                <ul className="space-y-1">
                  {highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[#2E1A12]/85">
                      <Check className="w-3.5 h-3.5 text-[#556B2F] flex-shrink-0 mt-0.5" />
                      {h}
                    </li>
                  ))}
                </ul>
              </td>
            </tr>
          )}

          {/* Admin detail rows */}
          {DETAIL_ROWS.filter(r => displayDetails[r.key]).map((r) => (
            <tr key={r.key} className="border border-[#5A2D1D]/20 border-t-0">
              <td className="px-3 py-2.5 font-bold text-[#556B2F] bg-[#EFD9B4]/60 align-top whitespace-nowrap">{r.label}</td>
              <td className="px-3 py-2.5 text-[#2E1A12]/85 leading-relaxed">{displayDetails[r.key]}</td>
            </tr>
          ))}

          {/* Category */}
          <tr className="border border-[#5A2D1D]/20 border-t-0">
            <td className="px-3 py-2.5 font-bold text-[#556B2F] bg-[#EFD9B4]/60 align-top whitespace-nowrap">Category</td>
            <td className="px-3 py-2.5">
              <span className="inline-flex items-center gap-1.5 text-[#2E1A12]/85">
                {product.category === "veg" ? (
                  <><span className="w-3 h-3 rounded-full bg-[#1a7a1a] border-2 border-[#1a7a1a] flex-shrink-0" />Vegetarian</>
                ) : (
                  <><span className="w-0 h-0 flex-shrink-0"
                    style={{ borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: "10px solid #b91c1c" }} />Non-Vegetarian</>
                )}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      {soldOut && product.stock_note && (
        <p className="text-xs text-[#7A3B25] font-semibold italic mb-4">{product.stock_note}</p>
      )}

        {/* Size + Add to Cart — compact row */}
        <div className="flex items-center gap-2 flex-wrap">
          {product.sizes.map((s) => {
            const active = s.label === size;
            return (
              <button key={s.label} type="button" disabled={soldOut}
                onClick={() => setSize(s.label)}
                className={`inline-flex flex-col items-center px-3 py-1.5 rounded-sm border text-xs font-semibold transition-colors disabled:opacity-50 ${
                  active ? "border-[#5A2D1D] bg-[#5A2D1D] text-[#F5E6CC]" : "border-[#5A2D1D]/30 text-[#5A2D1D] hover:border-[#5A2D1D]"
                }`}>
                <span className="flex items-center gap-0.5">
                  {active && <Check className="w-3 h-3 flex-shrink-0" />}
                  {s.label}
                </span>
                <span className="opacity-75 text-[11px]">{formatPrice(s.price)}</span>
              </button>
            );
          })}
          <button data-testid={`details-add-to-cart-${product.slug}`} disabled={soldOut} onClick={handleAdd}
            className="ml-auto inline-flex items-center gap-1.5 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/30 disabled:cursor-not-allowed text-[#F5E6CC] px-4 py-2 rounded-sm text-sm font-semibold transition-colors">
            {soldOut ? "Sold Out" : (<><Plus className="w-3.5 h-3.5" /> {t.cta.add_to_cart}</>)}
          </button>
        </div>
      </div>
  );
};

// Full-screen image lightbox — shows product image with name overlay.
// Closes on backdrop click, close button, or Escape key.
const ImageLightbox = ({ product, displayName, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/92 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close image"
        className="absolute top-4 right-4 bg-white/15 hover:bg-white/30 text-white rounded-full p-2.5 transition-colors"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Image container — stops backdrop click from closing when clicking the image itself */}
      <div
        className="relative max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full max-h-[80vh] object-contain rounded-sm shadow-2xl"
        />
        {/* Product name at bottom-centre */}
        <div className="absolute bottom-0 left-0 right-0 rounded-b-sm bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 pb-4 pt-12 pointer-events-none">
          <p className="text-center text-white font-display text-lg font-bold drop-shadow-lg">
            {displayName}
          </p>
        </div>
      </div>
    </div>
  );
};
